// ─────────────────────────────────────────────────────────────────────────────
// render-poster-svg.js — The Stars Above / poster engine (single source of truth)
//   computeLayout(opts)          → geometry in viewBox units (the shared brain)
//   renderPosterSVG(state, opts) → the poster SVG, built from that geometry
// Layout: MAP anchored near the top border; TEXT anchored near the bottom border;
// the middle gap grows as the poster gets taller. Circle is design-agnostic
// (pass slotImageHref = a star map, a moon, anything).
// ─────────────────────────────────────────────────────────────────────────────
(function (root) {
  'use strict';

  var VBW      = 1000;   // viewBox width — all geometry is relative to this
  var MAP_MAX  = 860;    // map diameter = 86% of width (consistent across sizes)
  var MAP_ZOOM = 1.2;    // crops the empty margin around the sky disc so it fills the
                         // frame. The fork ring rides the disc edge, so no gap. Raise
                         // for a bigger map; too high starts to crop the ring itself.
  var EDGE_GAP = 50;     // gap from the keyline border to the map (top) & text (bottom)
  var BORDER_FR = 0.03;  // keyline border inset as a fraction of content width
  var MIN_MID  = 40;     // minimum gap between map bottom and text (squarest sizes)

  // Stroke weights, as a fraction of viewBox width. Bump these to thicken the lines.
  var BORDER_W = 0.0026; // rectangular keyline stroke (drawn in editor AND render)
  var RING_W   = 0.0026; // ring around the star map (render/print only — editor ring is CSS)

  // Background gradient. Depth cue: lighten the top a touch, darken the bottom.
  // Set both to 0 for a flat fill. If the map circle looks darker than its surround
  // at the top, lower GRAD_TOP.
  var GRAD_TOP = 16;     // lighten the top by this many RGB levels
  var GRAD_BOT = -14;    // darken the bottom by this many RGB levels

  // Text stack (offsets in viewBox units, from the top of the text block)
  var T_HEADING_SIZE = 42, T_HEADING_DY = 42;
  var T_SUB_SIZE     = 24, T_SUB_DY     = 38;
  var T_DIV_DY       = 27, T_DIV_HALF   = 22;
  var T_CAP_SIZE     = 17, T_CAP_DY     = 31, T_CAP_LINE = 26;
  var TEXT_STACK     = T_HEADING_DY + T_SUB_DY + T_DIV_DY + T_CAP_DY + 2 * T_CAP_LINE; // last line baseline
  var TEXT_TAIL      = 14; // descender pad below the last line
  // No subheading: the heading drops into the space the subheading would have used,
  // so there's no empty gap above the divider. Slightly less than T_SUB_DY because
  // the heading is the larger type and needs a little more air above the divider.
  var T_NOSUB_SHIFT  = 30;

  // Optional details strip (PHASE | ILLUMINATION | MOON AGE) under the caption.
  // Columns must be wide enough for the longest value ("WAXING CRESCENT" is ~185
  // units at this size); at 150 they overlapped.
  var D_GAP = 78, D_LABEL_SIZE = 10, D_VALUE_SIZE = 15, D_VALUE_DY = 22, D_COL_HALF = 350;
  var D_STACK = D_GAP + D_VALUE_DY + 6;

  function computeLayout(opts) {
    opts = opts || {};
    var ratio = opts.ratio || 1.3333;
    var VBH = Math.round(VBW * ratio);

    var sx = (opts.safeInset && opts.safeInset.x) || 0;
    var sy = (opts.safeInset && opts.safeInset.y) || 0;
    var cx0 = VBW * sx, cy0 = VBH * sy;
    var cW  = VBW * (1 - 2 * sx), cH = VBH * (1 - 2 * sy);

    // The details strip takes vertical space, so the text block grows and the map
    // shrinks to suit — rather than the details overlapping the border.
    var textStack = TEXT_STACK + (opts.details ? D_STACK : 0);

    var bInset = cW * BORDER_FR;
    var mapTopY = cy0 + bInset + EDGE_GAP;                         // small gap under top border
    var textTop = cy0 + cH - bInset - EDGE_GAP - textStack - TEXT_TAIL; // small gap over bottom border

    var mapCx = cx0 + cW / 2;
    // 86% of width, but shrink only if it would crowd the text (squarest sizes only)
    var mapD = Math.min(MAP_MAX * (cW / VBW), (textTop - MIN_MID) - mapTopY);
    if (mapD < 0) mapD = 0;
    var mapY  = mapTopY;
    var mapCy = mapTopY + mapD / 2;
    var drawD = mapD * MAP_ZOOM;

    var ySub  = textTop + T_HEADING_DY + T_SUB_DY;
    var yHead = textTop + T_HEADING_DY + (opts.noSub ? T_NOSUB_SHIFT : 0);
    var yDiv  = ySub + T_DIV_DY;
    var yCap1 = yDiv + T_CAP_DY;

    return {
      vbW: VBW, vbH: VBH,
      content: { x: cx0, y: cy0, w: cW, h: cH },
      slot: { cx: mapCx, cy: mapCy, d: mapD, r: mapD / 2, drawD: drawD, drawX: mapCx - drawD/2, drawY: mapCy - drawD/2 },
      details: opts.details ? {
        labelY: yCap1 + 2*T_CAP_LINE + D_GAP,
        valueY: yCap1 + 2*T_CAP_LINE + D_GAP + D_VALUE_DY,
        cx: mapCx, colHalf: D_COL_HALF,
        labelSize: D_LABEL_SIZE, valueSize: D_VALUE_SIZE
      } : null,
      text: {
        heading: { x: mapCx, y: yHead, size: T_HEADING_SIZE },
        sub:     { x: mapCx, y: ySub,  size: T_SUB_SIZE },
        divider: { x1: mapCx - T_DIV_HALF, x2: mapCx + T_DIV_HALF, y: yDiv },
        caption: { x: mapCx, size: T_CAP_SIZE, lines: [yCap1, yCap1 + T_CAP_LINE, yCap1 + 2*T_CAP_LINE] }
      },
      border: { inset: bInset, width: VBW * BORDER_W }
    };
  }

  function esc(s){return String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');}
  function isLightBg(hex){if(!hex)return false;hex=hex.replace('#','');if(hex.length===3)hex=hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];var r=parseInt(hex.substr(0,2),16),g=parseInt(hex.substr(2,2),16),b=parseInt(hex.substr(4,2),16);if(isNaN(r))return false;return (0.2126*r+0.7152*g+0.0722*b)>150;}
  // Lighten (+d) or darken (-d) a hex colour by d RGB levels, clamped to 0..255.
  function shade(hex,d){hex=(hex||'#0a0a0a').replace('#','');if(hex.length===3)hex=hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];var r=parseInt(hex.substr(0,2),16),g=parseInt(hex.substr(2,2),16),b=parseInt(hex.substr(4,2),16);if(isNaN(r)){r=10;g=10;b=10;}function cl(v){return Math.max(0,Math.min(255,Math.round(v)));}function hx(v){return ('0'+cl(v).toString(16)).slice(-2);}return '#'+hx(r+d)+hx(g+d)+hx(b+d);}
  function fmtDate(d){var m=['January','February','March','April','May','June','July','August','September','October','November','December'];var n=d.getDate();var s=(n%100>=11&&n%100<=13)?'th':n%10===1?'st':n%10===2?'nd':n%10===3?'rd':'th';return n+s+' '+m[d.getMonth()]+' '+d.getFullYear();}
  function colors(bg){var l=isLightBg(bg);return{head:l?'rgba(15,25,45,0.95)':'rgba(255,255,255,0.95)',sub:l?'rgba(15,25,45,0.82)':'rgba(255,255,255,0.82)',div:l?'rgba(15,25,45,0.55)':'rgba(255,255,255,0.55)',cap:l?'rgba(15,25,45,0.72)':'rgba(255,255,255,0.72)',ring:l?'rgba(15,25,45,0.85)':'rgba(255,255,255,0.85)',bord:l?'rgba(15,25,45,0.45)':'rgba(255,255,255,0.55)'};}

  // opts: ratio, safeInset, slotImageHref (or mapImageHref), watermark, border, noRing,
  //       slotNoClip (don't clip the slot image to the circle — for a moon, whose glow
  //       needs to spill past the disc), slotScale (draw the slot image larger than the
  //       slot circle, e.g. 1.15 to leave room for that glow),
  //       glow: { colour, radius, alpha } — a big soft halo behind the slot, drawn on
  //       the background. This is the atmospheric hue on a moon print: large (radius is
  //       a multiple of the slot radius, ~2 is right), soft and subtle. Vector, so it
  //       prints perfectly at any size and needs no canvas filters.
  function renderPosterSVG(state, opts) {
    opts = opts || {};
    // Work out noSub from the state unless the caller set it, so every SVG render
    // (editor, enlarge, confirm, email preview) moves the heading the same way.
    if (opts.noSub == null) {
      var o2 = {}; for (var k in opts) o2[k] = opts[k];
      o2.noSub = !String(state.message || '').trim();
      opts = o2;
    }
    var L = computeLayout(opts);
    var c = colors(state.bgColor);
    var uid = 'sm' + Math.random().toString(36).slice(2, 8);
    var gid = 'bg' + uid;
    var href = opts.slotImageHref || opts.mapImageHref;
    var s = L.slot, t = L.text, p = [];
    var bg = state.bgColor || '#0a0a0a';
    var headFont = state.font || "'Cormorant Garamond', serif";
    var subFont  = state.subheadingFont || "'Cormorant Garamond', serif";
    // The subheading is optional: no placeholder, and the line is simply left out
    // when blank, so a print can never say "Your Subheading".
    var name = state.name || 'Your Heading', msg = String(state.message || '').trim() ? state.message : '';
    var loc = (state.locationName || '').toUpperCase(), coord = state.coordinates || '';
    var dateStr = state.date ? fmtDate(state.date).toUpperCase() : '';

    // Background: flat when both gradient deltas are 0, otherwise a subtle vertical
    // gradient. Midpoint sits at the base colour so the map circle blends into it.
    var flatBg = (GRAD_TOP === 0 && GRAD_BOT === 0);
    var bgFill = flatBg ? esc(bg) : ('url(#' + gid + ')');

    p.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + L.vbW + ' ' + L.vbH + '" width="' + L.vbW + '" height="' + L.vbH + '" preserveAspectRatio="xMidYMid meet">');
    p.push('<defs><clipPath id="' + uid + '"><circle cx="' + s.cx.toFixed(1) + '" cy="' + s.cy.toFixed(1) + '" r="' + s.r.toFixed(1) + '"/></clipPath>');
    if (!flatBg) {
      p.push('<linearGradient id="' + gid + '" x1="0" y1="0" x2="0" y2="1">');
      p.push('<stop offset="0" stop-color="' + shade(bg, GRAD_TOP) + '"/>');
      p.push('<stop offset="0.45" stop-color="' + esc(bg) + '"/>');
      p.push('<stop offset="1" stop-color="' + shade(bg, GRAD_BOT) + '"/>');
      p.push('</linearGradient>');
    }
    p.push('</defs>');
    p.push('<rect x="0" y="0" width="' + L.vbW + '" height="' + L.vbH + '" fill="' + bgFill + '"/>');

    // Atmospheric glow behind the slot — the soft hue on a moon print. Drawn straight
    // onto the background so it sits under everything, exactly like theirs.
    // The falloff follows a gaussian across many stops: three stops with linear
    // interpolation leaves a visible circular edge, which reads as a pasted-on disc.
    if (opts.glow && opts.glow.alpha > 0) {
      var gw = opts.glow, gId = 'glow' + uid;
      var gR = L.slot.r * (gw.radius || 2.6);
      var gCol = esc(gw.colour || '#8fb3ff');
      var stops = [];
      // NB: not `t` — that's L.text in this scope, and `var` isn't block-scoped.
      for (var gi = 0; gi <= 12; gi++) {
        var gt = gi / 12;
        var fall = Math.exp(-4.2 * gt * gt);        // smooth, edgeless
        stops.push('<stop offset="' + gt.toFixed(3) + '" stop-color="' + gCol +
                   '" stop-opacity="' + (gw.alpha * fall).toFixed(4) + '"/>');
      }
      p.push('<defs><radialGradient id="' + gId + '">' + stops.join('') + '</radialGradient></defs>');
      p.push('<circle cx="' + L.slot.cx.toFixed(1) + '" cy="' + L.slot.cy.toFixed(1) +
             '" r="' + gR.toFixed(1) + '" fill="url(#' + gId + ')"/>');
    }

    // Procedural starfield. Seeded, so the editor, the enlarge and the print all get
    // the SAME stars — a random field would differ between preview and print.
    if (opts.stars && opts.stars.count > 0) {
      var sd = (opts.stars.seed || 1) >>> 0;
      function rnd() {                              // mulberry32
        sd |= 0; sd = (sd + 0x6D2B79F5) | 0;
        var t = Math.imul(sd ^ (sd >>> 15), 1 | sd);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      }
      var sCol = esc(opts.stars.colour || '#ffffff');
      var sParts = [];
      for (var si = 0; si < opts.stars.count; si++) {
        var sx = rnd() * L.vbW, sy = rnd() * L.vbH;
        var mag = rnd();
        var sr = 0.5 + mag * mag * 2.2;             // a few big ones, mostly small
        var so = 0.25 + rnd() * 0.6;
        sParts.push('<circle cx="' + sx.toFixed(1) + '" cy="' + sy.toFixed(1) +
                    '" r="' + sr.toFixed(2) + '" fill="' + sCol + '" opacity="' + so.toFixed(2) + '"/>');
      }
      p.push(sParts.join(''));
    }
    if (href) {
      // slotNoZoom: the image is already cropped to the final view (from the editor),
      // so fill the slot 1:1. Otherwise apply MAP_ZOOM to crop a raw square map.
      var iD = opts.slotNoZoom ? s.d : s.drawD;
      if (opts.slotScale) iD = iD * opts.slotScale;
      var iX = s.cx - iD/2, iY = s.cy - iD/2;
      if (!opts.slotNoZoom && !opts.slotScale) { iX = s.drawX; iY = s.drawY; }
      var clipAttr = opts.slotNoClip ? '' : ' clip-path="url(#' + uid + ')"';
      p.push('<image href="' + esc(href) + '" x="' + iX.toFixed(1) + '" y="' + iY.toFixed(1) + '" width="' + iD.toFixed(1) + '" height="' + iD.toFixed(1) + '"' + clipAttr + ' preserveAspectRatio="xMidYMid slice"/>');
    }
    if (!opts.noRing) p.push('<circle cx="' + s.cx.toFixed(1) + '" cy="' + s.cy.toFixed(1) + '" r="' + s.r.toFixed(1) + '" fill="none" stroke="' + c.ring + '" stroke-width="' + (L.vbW*RING_W).toFixed(2) + '"/>');
    if (opts.border) p.push('<rect x="' + (L.content.x + L.border.inset).toFixed(1) + '" y="' + (L.content.y + L.border.inset).toFixed(1) + '" width="' + (L.content.w - 2*L.border.inset).toFixed(1) + '" height="' + (L.content.h - 2*L.border.inset).toFixed(1) + '" fill="none" stroke="' + c.bord + '" stroke-width="' + L.border.width.toFixed(2) + '"/>');
    // opts.watermark is available but unused — neither customiser passes it. Kept in
    // case a marketing render ever wants one.
    if (opts.watermark) {
      var wmSize = s.d * 0.085;
      p.push('<text x="' + s.cx.toFixed(1) + '" y="' + s.cy.toFixed(1) + '" transform="rotate(-24 ' + s.cx.toFixed(1) + ' ' + s.cy.toFixed(1) + ')" fill="rgba(255,255,255,0.13)" font-family="Montserrat, sans-serif" font-weight="300" font-size="' + wmSize.toFixed(1) + '" letter-spacing="' + (wmSize * 0.42).toFixed(1) + '" text-anchor="middle" dominant-baseline="middle">PREVIEW</text>');
    }
    p.push('<text x="' + t.heading.x.toFixed(1) + '" y="' + t.heading.y.toFixed(1) + '" fill="' + c.head + '" font-family="' + esc(headFont) + '" font-weight="300" font-size="' + t.heading.size + '" text-anchor="middle">' + esc(name) + '</text>');
    if (msg) p.push('<text x="' + t.sub.x.toFixed(1) + '" y="' + t.sub.y.toFixed(1) + '" fill="' + c.sub + '" font-family="' + esc(subFont) + '" font-style="italic" font-weight="300" font-size="' + t.sub.size + '" text-anchor="middle">' + esc(msg) + '</text>');
    p.push('<line x1="' + t.divider.x1.toFixed(1) + '" y1="' + t.divider.y.toFixed(1) + '" x2="' + t.divider.x2.toFixed(1) + '" y2="' + t.divider.y.toFixed(1) + '" stroke="' + c.div + '" stroke-width="1"/>');
    var ca = ' fill="' + c.cap + '" font-family="Montserrat, sans-serif" font-weight="500" font-size="' + t.caption.size + '" letter-spacing="0.6" text-anchor="middle"';
    if (loc)     p.push('<text x="' + t.caption.x.toFixed(1) + '" y="' + t.caption.lines[0].toFixed(1) + '"' + ca + '>' + esc(loc) + '</text>');
    if (coord)   p.push('<text x="' + t.caption.x.toFixed(1) + '" y="' + t.caption.lines[1].toFixed(1) + '"' + ca + '>' + esc(coord) + '</text>');
    if (dateStr) p.push('<text x="' + t.caption.x.toFixed(1) + '" y="' + t.caption.lines[2].toFixed(1) + '"' + ca + '>' + esc(dateStr) + '</text>');
    // Details strip: three columns with hairline dividers, mirroring the look of a
    // spec line under the caption.
    if (opts.details && L.details && opts.details.length) {
      var D = L.details, cols = opts.details.slice(0, 3);
      var span = D.colHalf * 2, step = span / cols.length;
      var x0 = D.cx - span/2 + step/2;
      for (var di = 0; di < cols.length; di++) {
        var dx = x0 + di*step;
        p.push('<text x="' + dx.toFixed(1) + '" y="' + D.labelY.toFixed(1) + '" fill="' + c.cap +
               '" font-family="Montserrat, sans-serif" font-weight="500" font-size="' + D.labelSize +
               '" letter-spacing="1.6" text-anchor="middle" opacity="0.75">' +
               esc(String(cols[di].label).toUpperCase()) + '</text>');
        p.push('<text x="' + dx.toFixed(1) + '" y="' + D.valueY.toFixed(1) + '" fill="' + c.head +
               '" font-family="Montserrat, sans-serif" font-weight="600" font-size="' + D.valueSize +
               '" letter-spacing="1.2" text-anchor="middle">' +
               esc(String(cols[di].value).toUpperCase()) + '</text>');
        if (di > 0) {
          var lx = dx - step/2;
          p.push('<line x1="' + lx.toFixed(1) + '" y1="' + (D.labelY - 9).toFixed(1) +
                 '" x2="' + lx.toFixed(1) + '" y2="' + (D.valueY + 4).toFixed(1) +
                 '" stroke="' + c.div + '" stroke-width="0.8" opacity="0.5"/>');
        }
      }
    }

    p.push('</svg>');
    return p.join('');
  }

  function ratioFromDims(d){return d.h/d.w;}
  function safeInsetFrac(full,safe){return {x:(1-safe.w/full.w)/2,y:(1-safe.h/full.h)/2};}

  // A real value, not a comment — Shopify minifies assets and strips comments, so a
  // commented marker can't tell you which build is live. Check in the console with:
  //    PosterSVG.VERSION
  var VERSION='nosub-2026-10-01';
  var api={VERSION:VERSION,computeLayout:computeLayout,renderPosterSVG:renderPosterSVG,ratioFromDims:ratioFromDims,safeInsetFrac:safeInsetFrac};
  if (typeof module!=='undefined'&&module.exports) module.exports=api;
  root.PosterSVG=api;
})(typeof window!=='undefined'?window:this);